import { supabase } from '../supabase';

export interface Wishlist {
  id: string;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface WishlistProperty {
  wishlist_id: string;
  property_id: string;
  created_at: string;
}

export interface SavedProperty {
  id: string;
  title: string;
  price: number;
  bond: number;
  bedrooms: number;
  bathrooms: number;
  parking: number;
  propertyType: string;
  description: string;
  features: string[];
  images: string[];
  availableFrom: string;
  featured: boolean;
  deposit: boolean;
  status?: string;
  location: {
    lat: number;
    lng: number;
    address: string;
    city: string;
    region: string;
  };
  agent?: {
    name: string;
    phone: string;
    email: string;
    agency: string;
    photo: string;
  };
  saved_at: string; // When it was added to wishlist
}

export class WishlistAPI {
  // Get or create default wishlist for user
  static async getOrCreateDefaultWishlist(userId: string): Promise<Wishlist> {
    console.log('🔄 WishlistAPI: getOrCreateDefaultWishlist called for user:', userId);
    console.log('🔄 WishlistAPI: User ID type:', typeof userId);
    console.log('🔄 WishlistAPI: User ID length:', userId?.length);
    console.log('🔄 WishlistAPI: User ID is valid UUID:', /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId));
    
    if (!userId || userId === 'undefined' || userId === 'null') {
      throw new Error('Invalid user ID provided to wishlist API');
    }

    // Verify user is authenticated with Supabase
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      console.error('❌ WishlistAPI: Authentication check failed:', authError);
      throw new Error('Auth session missing!');
    }

    if (user.id !== userId) {
      console.error('❌ WishlistAPI: User ID mismatch. Expected:', user.id, 'Got:', userId);
      throw new Error('User ID mismatch - authentication required');
    }
    
    // First try to get existing default wishlist
    const { data: existingWishlist, error: fetchError } = await supabase
      .from('wishlists')
      .select('*')
      .eq('user_id', userId)
      .eq('name', 'Saved Properties')
      .single();

    if (fetchError) {
      console.log('ℹ️ WishlistAPI: No existing wishlist found, will create new one');
    } else {
      console.log('✅ WishlistAPI: Found existing wishlist:', existingWishlist.id);
      return existingWishlist;
    }

    // Create default wishlist if it doesn't exist
    console.log('🔄 WishlistAPI: Creating new default wishlist...');
    const { data: newWishlist, error: createError } = await supabase
      .from('wishlists')
      .insert({
        user_id: userId,
        name: 'Saved Properties'
      })
      .select()
      .single();

    if (createError) {
      console.error('❌ WishlistAPI: Error creating default wishlist:', createError);
      throw createError;
    }

    console.log('✅ WishlistAPI: Created new wishlist:', newWishlist.id);
    return newWishlist;
  }

  // Get all saved properties for a user
  static async getSavedProperties(userId: string): Promise<SavedProperty[]> {
    console.log('🔄 WishlistAPI: getSavedProperties called for user:', userId);
    
    try {
      // Get the default wishlist
      const wishlist = await this.getOrCreateDefaultWishlist(userId);
      console.log('✅ WishlistAPI: Got wishlist:', wishlist.id);

      // Get all properties in the wishlist with full property details
      console.log('🔄 WishlistAPI: Fetching properties from wishlist...');
      const { data, error } = await supabase
        .from('wishlist_properties')
        .select(`
          created_at,
          properties (
            id,
            title,
            description,
            property_type,
            room_type,
            max_guests,
            bedroom_count,
            bed_count,
            bathroom_count,
            location,
            address,
            base_price,
            cleaning_fee,
            security_deposit,
            photos,
            status,
            metadata,
            created_at,
            updated_at
          )
        `)
        .eq('wishlist_id', wishlist.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ WishlistAPI: Error fetching saved properties:', error);
        throw error;
      }

      console.log('✅ WishlistAPI: Raw data from database:', data);

      // Transform the data to match our interface
      const transformed = (data || []).map(item => {
        const prop = item.properties as any; // Type assertion for now - TODO: fix typing
        
        // Debug photos field
        console.log('🔄 WishlistAPI: Processing property photos:', {
          propertyId: prop.id,
          photos: prop.photos,
          photosType: typeof prop.photos,
          photosLength: Array.isArray(prop.photos) ? prop.photos.length : 'not array'
        });
        
        // Debug price field
        console.log('🔄 WishlistAPI: Processing property price:', {
          propertyId: prop.id,
          base_price: prop.base_price,
          base_priceType: typeof prop.base_price
        });
        
        // Extract images from photos field
        let images: string[] = [];
        if (prop.photos) {
          if (Array.isArray(prop.photos)) {
            images = prop.photos
              .filter((p: any) => p !== null && p !== undefined) // Filter out null/undefined values
              .map((p: any) => {
                if (typeof p === 'string') {
                  return p;
                } else if (p && typeof p === 'object' && p.url) {
                  return p.url;
                } else if (p && typeof p === 'object' && p.photo_url) {
                  return p.photo_url;
                }
                return '';
              })
              .filter((url: string) => url && typeof url === 'string' && url.trim() !== '');
          } else if (typeof prop.photos === 'string') {
            // Handle case where photos might be a JSON string
            try {
              const parsedPhotos = JSON.parse(prop.photos);
              if (Array.isArray(parsedPhotos)) {
                images = parsedPhotos
                  .filter((p: any) => p !== null && p !== undefined) // Filter out null/undefined values
                  .map((p: any) => {
                    if (typeof p === 'string') {
                      return p;
                    } else if (p && typeof p === 'object' && p.url) {
                      return p.url;
                    } else if (p && typeof p === 'object' && p.photo_url) {
                      return p.photo_url;
                    }
                    return '';
                  })
                  .filter((url: string) => url && typeof url === 'string' && url.trim() !== '');
              }
            } catch {
              console.log('⚠️ WishlistAPI: Could not parse photos JSON:', prop.photos);
            }
          }
        }
        
        // Ensure images is always an array
        if (!Array.isArray(images)) {
          images = [];
        }
        
        console.log('✅ WishlistAPI: Extracted images:', images);
        
        return {
          id: prop.id || '',
          title: prop.title || 'Untitled Property',
          price: typeof prop.base_price === 'number' ? prop.base_price : 0,
          bond: 0, // Default value
          bedrooms: typeof prop.bedroom_count === 'number' ? prop.bedroom_count : 0,
          bathrooms: typeof prop.bathroom_count === 'number' ? prop.bathroom_count : 0,
          parking: 0, // Default value
          propertyType: prop.property_type || 'Unknown',
          description: prop.description || '',
          features: [], // Default empty array
          images: images,
          availableFrom: new Date('2025-08-14').toISOString(), // Set to August 14, 2025
          featured: false, // Default value
          deposit: false, // Default value
          status: prop.status || 'active',
          location: {
            lat: typeof prop.location?.coordinates?.[1] === 'number' ? prop.location.coordinates[1] : 0, // PostGIS stores as [lng, lat]
            lng: typeof prop.location?.coordinates?.[0] === 'number' ? prop.location.coordinates[0] : 0, // PostGIS stores as [lng, lat]
            address: prop.address?.street || prop.address?.full_address || '',
            city: prop.address?.city || '',
            region: prop.address?.state || prop.address?.region || ''
          },
          agent: {
            name: 'Property Owner', // Default value
            phone: '', // Default value
            email: '', // Default value
            agency: '', // Default value
            photo: '' // Default value
          },
          saved_at: item.created_at || new Date().toISOString()
        };
      });

      console.log('✅ WishlistAPI: Transformed properties:', transformed.length);
      return transformed;
    } catch (error) {
      console.error('❌ WishlistAPI: Error in getSavedProperties:', error);
      throw error;
    }
  }

  // Save a property to wishlist
  static async saveProperty(userId: string, propertyId: string): Promise<void> {
    console.log('💾 WishlistAPI: saveProperty called for user:', userId, 'property:', propertyId);
    console.log('💾 WishlistAPI: User ID type:', typeof userId);
    console.log('💾 WishlistAPI: User ID length:', userId?.length);
    console.log('💾 WishlistAPI: User ID is valid UUID:', /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId));
    
    if (!userId || userId === 'undefined' || userId === 'null') {
      throw new Error('Invalid user ID provided to wishlist API');
    }
    
    if (!propertyId || typeof propertyId !== 'string') {
      throw new Error('Invalid property ID provided to wishlist API');
    }

    // Verify user is authenticated
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user || user.id !== userId) {
      console.error('❌ WishlistAPI: Authentication failed for saveProperty');
      throw new Error('Authentication required to save properties');
    }
    
    try {
      // Get or create default wishlist
      const wishlist = await this.getOrCreateDefaultWishlist(userId);
      console.log('✅ WishlistAPI: Got wishlist for saving:', wishlist.id);

      // Check if property is already saved
      console.log('🔄 WishlistAPI: Checking if property is already saved...');
      const { data: existing, error: checkError } = await supabase
        .from('wishlist_properties')
        .select('property_id')
        .eq('wishlist_id', wishlist.id)
        .eq('property_id', propertyId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('❌ WishlistAPI: Error checking if property exists:', checkError);
        throw checkError;
      }

      if (existing) {
        console.log('ℹ️ WishlistAPI: Property is already saved, no action needed');
        // Property is already saved
        return;
      }

      // Add property to wishlist
      console.log('🔄 WishlistAPI: Adding property to wishlist...');
      const { error: insertError } = await supabase
        .from('wishlist_properties')
        .insert({
          wishlist_id: wishlist.id,
          property_id: propertyId
        });

      if (insertError) {
        console.error('❌ WishlistAPI: Error saving property:', insertError);
        throw insertError;
      }

      console.log('✅ WishlistAPI: Property saved successfully');
    } catch (error) {
      console.error('❌ WishlistAPI: Error in saveProperty:', error);
      throw error;
    }
  }

  // Remove a property from wishlist
  static async removeProperty(userId: string, propertyId: string): Promise<void> {
    if (!userId || userId === 'undefined' || userId === 'null') {
      throw new Error('Invalid user ID provided to wishlist API');
    }
    
    if (!propertyId || typeof propertyId !== 'string') {
      throw new Error('Invalid property ID provided to wishlist API');
    }

    // Verify user is authenticated
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user || user.id !== userId) {
      console.error('❌ WishlistAPI: Authentication failed for removeProperty');
      throw new Error('Authentication required to remove properties');
    }
    
    try {
      // Get the default wishlist
      const wishlist = await this.getOrCreateDefaultWishlist(userId);

      // Remove property from wishlist
      const { error } = await supabase
        .from('wishlist_properties')
        .delete()
        .eq('wishlist_id', wishlist.id)
        .eq('property_id', propertyId);

      if (error) {
        console.error('Error removing property:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in removeProperty:', error);
      throw error;
    }
  }

  // Check if a property is saved by the user
  static async isPropertySaved(userId: string, propertyId: string): Promise<boolean> {
    if (!userId || userId === 'undefined' || userId === 'null') {
      throw new Error('Invalid user ID provided to wishlist API');
    }
    
    if (!propertyId || typeof propertyId !== 'string') {
      throw new Error('Invalid property ID provided to wishlist API');
    }

    // Verify user is authenticated
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user || user.id !== userId) {
      console.error('❌ WishlistAPI: Authentication failed for isPropertySaved');
      return false; // Return false instead of throwing for this method
    }
    
    try {
      // Get the default wishlist
      const wishlist = await this.getOrCreateDefaultWishlist(userId);

      // Check if property exists in wishlist
      const { data, error } = await supabase
        .from('wishlist_properties')
        .select('property_id')
        .eq('wishlist_id', wishlist.id)
        .eq('property_id', propertyId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error checking if property is saved:', error);
        throw error;
      }

      return !!data;
    } catch (error) {
      console.error('Error in isPropertySaved:', error);
      return false;
    }
  }

  // Get saved properties count for a user
  static async getSavedPropertiesCount(userId: string): Promise<number> {
    if (!userId || userId === 'undefined' || userId === 'null') {
      throw new Error('Invalid user ID provided to wishlist API');
    }

    // Verify user is authenticated
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user || user.id !== userId) {
      console.error('❌ WishlistAPI: Authentication failed for getSavedPropertiesCount');
      return 0; // Return 0 instead of throwing for this method
    }
    
    try {
      // Get the default wishlist
      const wishlist = await this.getOrCreateDefaultWishlist(userId);

      // Count properties in wishlist
      const { count, error } = await supabase
        .from('wishlist_properties')
        .select('*', { count: 'exact', head: true })
        .eq('wishlist_id', wishlist.id);

      if (error) {
        console.error('Error counting saved properties:', error);
        throw error;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getSavedPropertiesCount:', error);
      return 0;
    }
  }
}
